define(['../converters', '../validators', './routingParameters', '../utils', 'common/parameterApplications'],
    function(converters, validators, getRoutingParameters, utils, parameterApplications) {
    /**
     * A setter and getter for `routeType` attribute.
     *
     * Represent the type of route requested.
     *
     * Notes on specific values:
     *  - `fastest` returns the fastest route.
     *  - `shortest` returns the shortest route by distance.
     *  - `eco` routes balance economy and speed.
     *  - thrilling routes include interesting or challenging roads and use as few motorways as possible. You can
     * choose the level of turns included and also the degree of hilliness. See the hilliness and windingness
     * parameters for how to set this. There is a limit of 900 km on routes planned with routeType=thrilling
     * Default value is fastest
     *
     * @method routeType
     * @chainable
     * @param {String} [routeType] Route type to be used during route calculation.
     * @throws {TypeError} if the given argument cannot be converted to a valid value
     * @return {String} Method returns the service instance allowing chaining or route type value if
     *     argument is omitted.
     */

    /**
     * A setter and getter for 'traffic' attribute.
     * Possible values:
     * true (do consider all available traffic information during routing)
     * false (ignore current traffic data during routing). Note that although the current traffic data is
     * ignored during routing, the effect of historic traffic on effective road speeds is still incorporated.
     * Default is true
     *
     * @method traffic
     * @chainable
     * @param {String|Boolean} [traffic] Boolean flag determine if traffic data should be used to calculate the
     *     route.
     * @throws {TypeError} if the given argument cannot be converted to a valid value
     * @return {Boolean} Method returns the service instance allowing chaining or traffic value if
     *     argument is omitted.
     */

    /**
     * A setter and getter for `avoid` attribute.
     *
     * Specifies something that the route calculation should try to avoid when determining the route.
     * Can be specified multiple times. Possible values:
     *  - `tollRoads` avoids toll roads.
     *  - `motorways` avoids motorways.
     *  - `ferries` avoids ferries.
     *  - `unpavedRoads` avoids unpaved roads.
     *  - `carpools` avoids routes that require use of carpool (HOV/ High Occupancy Vehicle) lanes.
     *  - `alreadyUsedRoads` avoids using the same road multiple times.
     *
     * Most useful in conjunction with `routeType=thrilling`.
     *
     * @method avoid
     * @chainable
     * @param {Array|String} [avoid] Specifies something that the route calculation should try to avoid when
     *     determining the route
     * @throws {TypeError} if the given argument cannot be converted to a valid value
     * @return {Array} Method returns the service instance allowing chaining or street number value if
     *     argument is omitted.
     */

    /**
     * A setter and getter for 'departAt' attribute.
     * The date and time of departure from the origin point.
     * Departure times apart from now must be specified as a dateTime.
     * When a time zone offset is not specified, it will be assumed to be that of the origin point.
     * The departAt value must be in the future. The departAt parameter cannot be used in conjunction with
     * arriveAt.
     *
     * @method departAt
     * @chainable
     * @param {String} [departAt] The properly formatted date with time or 'now' value. Date cannot be in the
     *     past.
     * @throws {TypeError} if the given argument cannot be converted to a valid value
     * @return {String} Method returns the service instance allowing chaining or language if argument
     *     is omitted.
     */

    /**
     * A setter and getter for 'arriveAt' attribute.
     * The date and time of arrival at the destination point.
     * It must be specified as a dateTime.
     * When a time zone offset is not specified it will be assumed to be that of the destination point.
     * The arriveAt value must be in the future. The arriveAt parameter cannot be used in conjunction with
     * departAt.
     *
     * @method arriveAt
     * @chainable
     * @param {String} [arriveAt] The properly formatted date with time value. Date cannot be in the past.
     * @throws {TypeError} if the given argument cannot be converted to a valid value
     * @return {String} Method returns the service instance allowing chaining or language if argument
     *     is omitted.
     */

    /**
     * A setter and getter for 'travelMode' attribute.
     * The mode of travel for the requested route. Possible values: _car, truck, taxi, bus, van, motorcycle,
     * bicycle, pedestrian_ Note that the requested travelMode may not be available for the entire route. Where
     * the requested travelMode is not available for a particular section, the <travelMode> element of the
     * response for that section will be other.
     *
     * @method travelMode
     * @chainable
     * @param {String} [travelMode] The primary mean of transportation to be used while routing.
     * @throws {TypeError} if the given argument cannot be converted to a valid value
     * @return {String} Method returns the service instance allowing chaining or language if argument
     *     is omitted.
     */

    /**
     * A setter and getter for 'hilliness' attribute.
     * Degree of hilliness for thrilling route. Possible values: low, normal, high
     * This parameter can only be used in conjunction with routeType thrilling.
     *
     * @method hilliness
     * @chainable
     * @param {String} [hilliness] The level of hilliness on a thrilling route.
     * @throws {TypeError} if the given argument cannot be converted to a valid value
     * @return {String} Method returns the service instance allowing chaining or language if argument
     *     is omitted.
     */

    /**
     * A setter and getter for 'windingness' attribute.
     * Level of turns for thrilling route. Possible values: low, normal, high
     * This parameter can only be used in conjunction with routeType thrilling.
     *
     * @method windingness
     * @chainable
     * @param {String} [windingness] The level of windingness on a thrilling route.
     * @throws {TypeError} if the given argument cannot be converted to a valid value
     * @return {String} Method returns the service instance allowing chaining or language if argument
     *     is omitted.
     */

    /**
     * A setter and getter for 'computeTravelTimeFor' attribute.
     * Specifies whether to return additional travel times using different types of traffic information (none,
     * historic, live) as well as the default best-estimate travel time. Possible values:
     * * none - do not compute additional travel times,
     * * all - compute travel times for all types of traffic information. Specifying all results in the fields
     * noTrafficTravelTimeInSeconds, historicTrafficTravelTimeInSeconds and
     * liveTrafficIncidentsTravelTimeInSeconds being included in the summaries in the route response.
     *
     * @method computeTravelTimeFor
     * @chainable
     * @param {String} [computeTravelTimeFor] Setting _all_ provides additional information regards travel
     *     times.
     * @throws {TypeError} if the given argument cannot be converted to a valid value
     * @return {String} Method returns the service instance allowing chaining or current value if
     *     argument is omitted.
     */

    /**
     * A setter and getter for 'vehicleEngineType' attribute.
     * Engine type of the vehicle. This parameter is currently only considered for travelMode truck. Valid
     * options are: _combustion, electric_
     *
     * @method vehicleEngineType
     * @chainable
     * @param {String} [vehicleEngineType] The vehicle engine type.
     * @throws {TypeError} if the given argument cannot be converted to a valid value
     * @return {String} Method returns the service instance allowing chaining or current value if
     *     argument is omitted.
     */

    /**
     * A setter and getter for 'vehicleMaxSpeed' attribute.
     * Maximum speed of the vehicle in km/hour. A value of 0 means that an appropriate value for the vehicle
     * will be determined and applied during route planning. A non-zero value may be overridden during route
     * planning. This parameter is currently only considered for travelMode truck.
     *
     * @method vehicleMaxSpeed
     * @chainable
     * @param {Number} [vehicleMaxSpeed] The maximal allowed vehicle speed in km/h.
     * @throws {TypeError} if the given argument cannot be converted to a valid value
     * @return {Number} Method returns the service instance allowing chaining or current value if
     *     argument is omitted.
     */

    /**
     * A setter and getter for 'vehicleWeight' attribute.
     * Weight of the vehicle in kg. A value of 0 means that weight restrictions are not considered during route
     * planning. This parameter is currently only considered for travelMode truck.
     *
     * @method vehicleWeight
     * @chainable
     * @param {Number} [vehicleWeight] The vehicle weight in kg.
     * @throws {TypeError} if the given argument cannot be converted to a valid value
     * @return {Number} Method returns the service instance allowing chaining or current value if
     *     argument is omitted.
     */

    /**
     * A setter and getter for 'vehicleAxleWeight' attribute.
     * Weight per axle of the vehicle in kg. A value of 0 means that weight restrictions per axle are not
     * considered during route planning. This parameter is currently only considered for travelMode truck.
     *
     * @method vehicleAxleWeight
     * @chainable
     * @param {Number} [vehicleAxleWeight] The vehicle axle wight in kg.
     * @throws {TypeError} if the given argument cannot be converted to a valid value
     * @return {Number} Method returns the service instance allowing chaining or current value if
     *     argument is omitted.
     */

    /**
     * A setter and getter for 'vehicleLength' attribute.
     * Length of the vehicle in meters. A value of 0 means that length restrictions are not considered during
     * route planning. This parameter is currently only considered for travelMode truck.
     *
     * @method vehicleLength
     * @chainable
     * @param {Number} [vehicleLength] The vehicle length in m.
     * @throws {TypeError} if the given argument cannot be converted to a valid value
     * @return {Number} Method returns the service instance allowing chaining or current value if
     *     argument is omitted.
     */

    /**
     * A setter and getter for 'vehicleWidth' attribute.
     * Width of the vehicle in meters. A value of 0 means that width restrictions are not considered during
     * route planning. This parameter is currently only considered for travelMode truck.
     *
     * @method vehicleWidth
     * @chainable
     * @param {Number} [vehicleWidth] The vehicle width in m.
     * @throws {TypeError} if the given argument cannot be converted to a valid value
     * @return {Number} Method returns the service instance allowing chaining or current value if
     *     argument is omitted.
     */

    /**
     * A setter and getter for 'vehicleHeight' attribute.
     * Height of the vehicle in meters. A value of 0 means that height restrictions are not considered
     * during route planning. This parameter is currently only considered for travelMode truck.
     *
     * @method vehicleHeight
     * @chainable
     * @param {Number} [vehicleHeight] The vehicle height in m.
     * @throws {TypeError} if the given argument cannot be converted to a valid value
     * @return {Number} Method returns the service instance allowing chaining or current value if
     *     argument is omitted.
     */

    /**
     * A setter and getter for 'vehicleCommercial' attribute.
     * Vehicle is used for commercial purposes and thus may not be allowed to drive on some roads. This
     * parameter is currently only considered for travelMode truck.
     *
     * @method vehicleCommercial
     * @chainable
     * @param {Boolean} [vehicleCommercial] True if vehicle is used for commercial purposes.
     * @throws {TypeError} if the given argument cannot be converted to a valid value
     * @return {String} Method returns the service instance allowing chaining or current value if
     *     argument is omitted.
     */

    /**
     * A setter and getter for 'vehicleLoadType' attribute.
     * Types of cargo that may be classified as hazardous materials and restricted from some roads. Available
     * vehicleLoadType values are US Hazmat classes 1 through 9, plus generic classifications for use in other
     * countries.
     *
     * __Use these for routing in US__
     * * USHazmatClass1 Explosives
     * * USHazmatClass2 Compressed gas
     * * USHazmatClass3 Flammable liquids
     * * USHazmatClass4 Flammable solids
     * * USHazmatClass5 Oxidizers
     * * USHazmatClass6 Poisons
     * * USHazmatClass7 Radioactive
     * * USHazmatclass8 Corrosives
     * * USHazmatClass9 Miscellaneous
     *
     * __Use these for routing in all other countries__
     * * otherHazmatExplosive Explosives
     * * otherHazmatGeneral Miscellaneous
     * * otherHazmatHarmfulToWater Harmful to water
     *
     * This parameter is currently only considered for travelMode truck.
     *
     * @method vehicleLoadType
     * @chainable
     * @param {Array|String} [vehicleLoadType] The array of truck cargo classification.
     * @throws {TypeError} if the given argument cannot be converted to a valid value
     * @return {Array} Method returns the service instance allowing chaining or current value if
     *     argument is omitted.
     */

        var fields = getRoutingParameters(['locations', 'maxAlternatives', 'instructionsType',
            'language', 'computeBestOrder', 'routeRepresentation', 'vehicleHeading', 'report',
            'callback', 'minDeviationTime', 'minDeviationDistance', 'alternativeType'
        ]);

        var latLonFieldDescription = {
            validator: validators.arrayOf({
                point: {
                    validator: validators.objectOf({
                        latitude: {
                            validator: validators.latitude,
                            required: true
                        },
                        longitude: {
                            validator: validators.longitude,
                            required: true
                        }
                    })
                }
            }),
            converter: converters.arrayOf({
                point: {
                    converter: converters.objectOf({
                        latitude: {
                            converter: converters.latitude,
                            required: true
                        },
                        longitude: {
                            converter: converters.longitude,
                            required: true
                        }
                    })
                }
            }),
            required: true,
            application: parameterApplications.POST
        };

        /**
     * A setter and getter for 'origins' attribute.
     * A set of origin locations represented by points (lat,lon). At least one origin is required.
     *
     * @method origins
     * @chainable
     * @param {Array} [origins] A set of origin locations represented by points (lat,lon).
     * @throws {TypeError} if the given argument cannot be converted to a valid value
     * @return {String} Method returns the service instance allowing chaining or current value if
     *     argument is omitted.
     */
        fields.origins = latLonFieldDescription;

        /**
     * A setter and getter for 'destinations' attribute.
     * A set of destination locations represented by points (lat,lon). At least one destination is required.
     *
     * @method destinations
     * @chainable
     * @param {Array} [destinations] A set of origin locations represented by points (lat,lon).
     * @throws {TypeError} if the given argument cannot be converted to a valid value
     * @return {String} Method returns the service instance allowing chaining or current value if
     *     argument is omitted.
     */
        fields.destinations = latLonFieldDescription;

        return function(fieldsToSkip) {
            var result = utils.clone(fields);
            if (fieldsToSkip instanceof Array) {
                fieldsToSkip.forEach(function(field) {
                    delete result[field];
                });
            }

            return result;
        };
    });



// WEBPACK FOOTER //
// ./services/routing/matrixParameters.js